import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router';
import { useState } from 'react';
import { Page } from '@/components/Page';
import { RouteError } from '@/components/RouteError';
import {
    SurveyPermissionEditor,
    SurveyPermissionEditorSkeleton,
} from '@/components/surveys/permissions/SurveyPermissionEditor';
import { SurveySelector, SurveySelectorSkeleton } from '@/components/surveys/permissions/SurveySelector';
import { fetchSurveys } from '@/queries/surveys/client';
import { fetchUser } from '@/queries/users';
import { getFullName } from '@/utils';
import {
    useRevokeUserSurveyPermission,
    useSaveUserSurveyPermission,
    useSurveyAttributes,
    useUserSurveyPermissions,
} from './-hooks';
import * as styles from './index.css';

export const Route = createFileRoute('/_client/_auth/users/$userId/survey-permissions/')({
    component: ClientUserSurveyPermissionsRoute,
    loader: async ({ params: { userId } }) => {
        const surveys = await fetchSurveys();
        const user = await fetchUser(userId);

        return { surveys: surveys.surveys, user };
    },
    pendingComponent: ClientUserSurveyPermissionsRouteSkeleton,
    errorComponent: ClientUserSurveyPermissionsRouteError,
});

function ClientUserSurveyPermissionsRouteError({ reset }: ErrorComponentProps) {
    return <RouteError kind="client" title="閲覧権限" onRetry={reset} />;
}

/**
 * Bails out before `Content` mounts — and its permission/attribute queries
 * fire — when there are no surveys to show.
 */
function ClientUserSurveyPermissionsRoute() {
    const { surveys } = Route.useLoaderData();

    if (surveys.length === 0) {
        return (
            <Page.Root>
                <Page.Header title="閲覧権限" />

                <Page.Content gap="5">
                    <Page.BackLink to="/users" label="ユーザー管理に戻る" />
                    <p className={styles.emptyState}>公開中のサーベイがありません。</p>
                </Page.Content>
            </Page.Root>
        );
    }

    return <Content />;
}

function Content() {
    const { userId } = Route.useParams();
    const { surveys, user } = Route.useLoaderData();
    const [selectedSurveyId, setSelectedSurveyId] = useState(surveys[0]?.surveyId ?? '');

    const attributesQuery = useSurveyAttributes(selectedSurveyId);
    const permissionsQuery = useUserSurveyPermissions(userId);
    const saveMutation = useSaveUserSurveyPermission(userId);
    const revokeMutation = useRevokeUserSurveyPermission(userId);

    const selectedSurvey = surveys.find((survey) => survey.surveyId === selectedSurveyId) ?? surveys[0];
    const isSaving = saveMutation.isPending || revokeMutation.isPending;

    return (
        <Page.Root>
            <Page.Header title={`${getFullName(user)} の閲覧権限`} />

            <Page.Content gap="5">
                <Page.BackLink to="/users" label="ユーザー管理に戻る" />

                <SurveySelector
                    surveys={surveys}
                    permissions={permissionsQuery}
                    selectedSurveyId={selectedSurveyId}
                    onSelectSurvey={setSelectedSurveyId}
                    disabled={isSaving}
                />

                <SurveyPermissionEditor
                    survey={selectedSurvey}
                    attributes={attributesQuery}
                    permissions={permissionsQuery}
                    isSaving={isSaving}
                    onSave={(surveyId, input) => saveMutation.mutate({ surveyId, input })}
                    onRevoke={(surveyId) => revokeMutation.mutate(surveyId)}
                />
            </Page.Content>
        </Page.Root>
    );
}

function ClientUserSurveyPermissionsRouteSkeleton() {
    return (
        <Page.Root>
            {/* The title and back link are static, so they render for real rather than as placeholders. */}
            <Page.Header title="閲覧権限" />

            <Page.Content gap="5">
                <Page.BackLink to="/users" label="ユーザー管理に戻る" />

                <SurveySelectorSkeleton />

                <SurveyPermissionEditorSkeleton />
            </Page.Content>
        </Page.Root>
    );
}
